import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import fieldWorkService from '../../services/fieldWork';
import {
  ACTIVITY_CATEGORY_LABELS,
  WORK_UNIT_LABELS,
  type ActivityCategory,
  type ActivityType,
  type WorkUnit,
} from '../../types/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import FormModal, { Field, inputClass } from './FormModal';

const CATEGORIES = Object.keys(ACTIVITY_CATEGORY_LABELS) as ActivityCategory[];
const UNITS = Object.keys(WORK_UNIT_LABELS) as WorkUnit[];

/**
 * Types of work. Built-ins are shared by every org and read-only; a Farm Jobs
 * Admin adds the org's own and can retire them.
 */
export default function ActivityTypesPanel({ canEdit }: { canEdit: boolean }) {
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setTypes(await fieldWorkService.getActivityTypes(true));
    } catch (e) {
      setError(errorMessage(e, 'Could not load work types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (t: ActivityType) => {
    try {
      const updated = await fieldWorkService.updateActivityType(t.id, { active: !t.active });
      setTypes(list => list.map(x => (x.id === t.id ? updated : x)));
    } catch (e) {
      setError(errorMessage(e, 'Could not update that work type'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-neutral-500">
          {canEdit ? 'Built-in types are shared by everyone. Add your own for work that is not listed.' : 'Adding work types needs a Farm Jobs Admin seat.'}
        </p>
        {canEdit && (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> New type
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Loading…</p>
      ) : (
        <div className="space-y-4">
          {CATEGORIES.map(cat => {
            const items = types.filter(t => t.category === cat);
            if (!items.length) return null;
            return (
              <section key={cat}>
                <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">{ACTIVITY_CATEGORY_LABELS[cat]}</h3>
                <ul className="flex flex-wrap gap-2">
                  {items.map(t => (
                    <li key={t.id} className={`text-sm px-3 py-1.5 rounded-lg border flex items-center gap-2 ${t.active ? 'border-neutral-200 bg-white' : 'border-dashed border-neutral-300 bg-neutral-50 text-neutral-500'}`}>
                      <span>{t.name}</span>
                      <span className="text-xs text-neutral-400">{WORK_UNIT_LABELS[t.work_unit].toLowerCase()}</span>
                      {!t.is_builtin && canEdit && (
                        <button type="button" onClick={() => toggleActive(t)} className="text-xs text-indigo-600 hover:underline">
                          {t.active ? 'Retire' : 'Restore'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      {adding && (
        <NewActivityTypeModal
          onClose={() => setAdding(false)}
          onSaved={t => { setAdding(false); setTypes(list => [...list, t]); }}
        />
      )}
    </div>
  );
}

function NewActivityTypeModal({ onClose, onSaved }: { onClose: () => void; onSaved: (t: ActivityType) => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState<ActivityCategory>('general');
  const [unit, setUnit] = useState<WorkUnit>('hours');
  const [gps, setGps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await fieldWorkService.createActivityType({
        name: name.trim(), category, work_unit: unit, uses_gps_coverage: gps,
      }));
    } catch (e) {
      setError(errorMessage(e, 'Could not add the work type'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title="New work type" onClose={onClose} onSubmit={submit} submitting={saving}
      submitDisabled={!name.trim()} error={error}>
      <Field label="Name" htmlFor="at-name">
        <input id="at-name" className={inputClass} value={name} onChange={e => setName(e.target.value)} autoFocus />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Category" htmlFor="at-cat">
          <select id="at-cat" className={inputClass} value={category} onChange={e => setCategory(e.target.value as ActivityCategory)}>
            {CATEGORIES.map(c => <option key={c} value={c}>{ACTIVITY_CATEGORY_LABELS[c]}</option>)}
          </select>
        </Field>
        <Field label="Measured in" htmlFor="at-unit">
          <select id="at-unit" className={inputClass} value={unit} onChange={e => setUnit(e.target.value as WorkUnit)}>
            {UNITS.map(u => <option key={u} value={u}>{WORK_UNIT_LABELS[u]}</option>)}
          </select>
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={gps} onChange={e => setGps(e.target.checked)}
          className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
        <span className="text-neutral-700">Machine work — track GPS coverage by default</span>
      </label>
    </FormModal>
  );
}
